import React, { useMemo } from "react";
import { Head } from "zudoku/components";
import { useTenantStore } from "../stores/tenantStore";

import { Progress } from "zudoku/ui/Progress";
import { Card } from "zudoku/ui/Card";
import useGetTenantQuotas from "./useGetTenantQuotas";

export default function APIAnalyticsPage() {
  const { currentTenant } = useTenantStore();
  const { data: quotas, isFetching } = useGetTenantQuotas();

  const callPercent = quotas
    ? (parseInt(quotas.monthly.calls.current) /
        parseInt(quotas.monthly.calls.max)) *
      100
    : 0;

  const bandwidthPercent = quotas
    ? (parseFloat(quotas.monthly.kBs.current) /
        parseFloat(quotas.monthly.kBs.max)) *
      100
    : 0;

  const formattedCalls = useMemo(() => {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "decimal",
      maximumFractionDigits: 0,
    });

    if (!quotas || !quotas.monthly || !quotas.monthly.calls) {
      return "No call data available";
    }

    return `${formatter.format(
      parseInt(quotas.monthly.calls.current)
    )} / ${formatter.format(
      parseInt(quotas.monthly.calls.max)
    )} calls this month`;
  }, [quotas]);

  const formattedBandwidth = useMemo(() => {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "decimal",
      maximumFractionDigits: 3,
    });

    if (!quotas || !quotas.monthly || !quotas.monthly.kBs) {
      return "No bandwidth data available";
    }

    return `${formatter.format(
      parseFloat(quotas.monthly.kBs.current)
    )} / ${formatter.format(
      parseFloat(quotas.monthly.kBs.max)
    )} kBs this month`;
  }, [quotas]);

  return (
    <section className="w-full">
      <Head>
        <title>{import.meta.env.ZUDOKU_PUBLIC_PAGE_TITLE} - Analytics</title>
      </Head>
      <div className="py-4">
        <h1 className="text-2xl font-semibold">API Analytics</h1>

        {isFetching && (
          <p className="text-muted-foreground">Loading API analytics...</p>
        )}

        {!currentTenant && (
          <p className="text-muted-foreground">
            Please select a tenant to view API analytics
          </p>
        )}

        {currentTenant && !isFetching && quotas && (
          <>
            <Card className="py-4 px-4 my-4">
              <h2 className="mb-2 font-bold">Calls</h2>
              <Progress className="mb-2 h-2" value={callPercent} />
              <div className="text-sm text-right">{formattedCalls}</div>
            </Card>
            <Card className="py-4 px-4 my-4">
              <h2 className="mb-2 font-bold">Bandwidth</h2>
              <Progress className="mb-2 h-2" value={bandwidthPercent} />
              <div className="text-sm text-right">{formattedBandwidth}</div>
            </Card>
          </>
        )}
      </div>
    </section>
  );
}
